import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { formatTimeAgo, getInitials, cn } from '../lib/utils';
import axios from 'axios';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, Lock, Plus, Search, Send, 
  Loader2, Clock, Shield, MessageCircle 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecretChatsPage() {
  const { user, token, language } = useAuth();
  const tr = t(language);
  const navigate = useNavigate();
  
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [autoDeleteMinutes, setAutoDeleteMinutes] = useState(60);
  const [creating, setCreating] = useState(false);

  const fetchChats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/secret-chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChats(response.data);
    } catch (error) {
      console.error('Failed to fetch secret chats:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  const handleSearch = async (value) => {
    setSearchQuery(value);
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(value)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleCreateSecretChat = async (participantId) => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/secret-chats`, {
        participant_id: participantId,
        auto_delete_minutes: autoDeleteMinutes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowNewChat(false);
      setSearchQuery('');
      setSearchResults([]);
      navigate(`/secret-chat/${response.data.id}`);
    } catch (error) {
      toast.error('Error creating secret chat');
    } finally {
      setCreating(false);
    }
  };

  const getChatName = (chat) => {
    const other = chat.participants?.find(p => p.id !== user?.id);
    return other?.display_name || other?.username || 'Unknown';
  };

  const getChatAvatar = (chat) => {
    const other = chat.participants?.find(p => p.id !== user?.id);
    return other?.avatar || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="secret-chats-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Lock className="w-6 h-6 text-primary" />
              <h1 className="text-xl font-bold">Secret Chat</h1>
            </div>
          </div>
          <Button 
            size="icon" 
            className="rounded-full"
            onClick={() => setShowNewChat(true)}
            data-testid="new-secret-chat-btn"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Info Banner */}
      <div className="mx-4 mt-4 p-4 bg-primary/10 rounded-2xl border border-primary/20">
        <div className="flex items-start gap-3">
          <Shield className="w-6 h-6 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-primary">Disappearing Messages</p>
            <p className="text-sm text-muted-foreground mt-1">
              Messages in secret chats auto-delete after the time you choose. 
              No one can save them.
            </p>
          </div>
        </div>
      </div>

      {/* Chat List */}
      <div className="divide-y divide-border/40 mt-4">
        {chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <Lock className="w-16 h-16 text-muted-foreground mb-4" />
            <p className="text-lg font-medium text-center">No secret chats</p>
            <p className="text-muted-foreground text-center mt-2">
              Start a private conversation with disappearing messages
            </p>
            <Button 
              className="mt-6 rounded-full"
              onClick={() => setShowNewChat(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Secret Chat
            </Button>
          </div>
        ) : (
          chats.map(chat => (
            <Link
              key={chat.id}
              to={`/secret-chat/${chat.id}`}
              className="flex items-center gap-3 p-4 hover:bg-secondary/30 transition-colors"
              data-testid={`secret-chat-${chat.id}`}
            >
              <div className="relative">
                <Avatar className="w-14 h-14 ring-2 ring-primary/30">
                  <AvatarImage src={getChatAvatar(chat)} alt={getChatName(chat)} />
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {getInitials(getChatName(chat))}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                  <Lock className="w-3 h-3" />
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold truncate">{getChatName(chat)}</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <Clock className="w-4 h-4" />
                  <span>Messages delete after {chat.auto_delete_minutes} min</span>
                </div>
              </div>
            </Link>
          ))
        )}
      </div>

      {/* New Secret Chat Modal */}
      <Dialog open={showNewChat} onOpenChange={setShowNewChat}>
        <DialogContent className="sm:max-w-md" data-testid="new-secret-chat-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              New Secret Chat
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {/* Auto-delete time */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Messages delete after:</label>
              <div className="flex gap-2">
                {[5, 15, 30, 60, 1440].map(minutes => (
                  <Button
                    key={minutes}
                    variant={autoDeleteMinutes === minutes ? "default" : "outline"}
                    size="sm"
                    className="rounded-full"
                    onClick={() => setAutoDeleteMinutes(minutes)}
                  >
                    {minutes < 60 ? `${minutes}min` : minutes === 60 ? '1h' : '24h'}
                  </Button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 rounded-2xl"
                data-testid="secret-chat-search"
              />
            </div>

            {/* Results */}
            <div className="max-h-[300px] overflow-y-auto space-y-1">
              {searching && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              )}

              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="text-center py-4 text-muted-foreground">No results</p>
              )}

              {searchResults.map(result => (
                <button
                  key={result.id}
                  onClick={() => handleCreateSecretChat(result.id)}
                  disabled={creating}
                  className="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-secondary/50 transition-colors"
                  data-testid={`user-result-${result.id}`}
                >
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={result.avatar} alt={result.display_name} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {getInitials(result.display_name || result.username)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-semibold truncate">{result.display_name || result.username}</p>
                    <p className="text-sm text-muted-foreground truncate">@{result.username}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
